import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressionTracker = ({ stats }) => {
  const achievements = [
    {
      id: 1,
      name: "First Victory",
      description: "Win your first game",
      icon: "Trophy",
      isUnlocked: stats?.totalWins >= 1,
      progress: Math.min(stats?.totalWins, 1),
      required: 1,
    },
    {
      id: 2,
      name: "Gem Collector",
      description: "Collect 50 gems",
      icon: "Gem",
      isUnlocked: stats?.totalGems >= 50,
      progress: Math.min(stats?.totalGems, 50),
      required: 50,
    },
    {
      id: 3,
      name: "Streak Master",
      description: "Achieve a 10-win streak",
      icon: "Flame",
      isUnlocked: stats?.bestStreak >= 10,
      progress: Math.min(stats?.bestStreak, 10),
      required: 10,
    },
    {
      id: 4,
      name: "Mood Explorer",
      description: "Unlock all AI moods",
      icon: "Brain",
      isUnlocked: stats?.unlockedMoods >= 5,
      progress: stats?.unlockedMoods,
      required: 5,
    },
  ];

  return (
    <div className="bg-card border-2 border-primary/50 rounded-sm p-4 md:p-6 shadow-glow-cyan-sm">
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <Icon name="Award" size={24} color="var(--color-primary)" />
        <h2 className="font-heading font-bold text-lg md:text-xl text-primary">Progression Tracker</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-background border border-accent/30 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Trophy" size={18} color="var(--color-accent)" />
            <span className="text-xs md:text-sm font-caption text-muted-foreground">Total Wins</span>
          </div>
          <p className="text-xl md:text-2xl font-data font-bold text-accent">{stats?.totalWins}</p>
        </div>

        <div className="p-3 md:p-4 bg-background border border-primary/30 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Gem" size={18} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-caption text-muted-foreground">Total Gems</span>
          </div>
          <p className="text-xl md:text-2xl font-data font-bold text-primary">{stats?.totalGems}</p>
        </div>

        <div className="p-3 md:p-4 bg-background border border-warning/30 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Flame" size={18} color="var(--color-warning)" />
            <span className="text-xs md:text-sm font-caption text-muted-foreground">Best Streak</span>
          </div>
          <p className="text-xl md:text-2xl font-data font-bold text-warning">{stats?.bestStreak}</p>
        </div>

        <div className="p-3 md:p-4 bg-background border border-secondary/30 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Brain" size={18} color="var(--color-secondary)" />
            <span className="text-xs md:text-sm font-caption text-muted-foreground">Unlocked Moods</span>
          </div>
          <p className="text-xl md:text-2xl font-data font-bold text-secondary">
            {stats?.unlockedMoods}/5
          </p>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        <h3 className="font-heading font-semibold text-sm md:text-base text-foreground">Achievements</h3>
        {achievements?.map((achievement) => (
          <div
            key={achievement?.id}
            className={`
              p-3 md:p-4 rounded-sm border transition-all duration-base
              ${achievement?.isUnlocked
                ? 'bg-accent/10 border-accent shadow-glow-green-sm'
                : 'bg-background border-muted/30'
              }
            `}
          >
            <div className="flex items-start gap-3">
              <div
                className={`
                  w-10 h-10 md:w-12 md:h-12 flex-shrink-0 rounded-sm flex items-center justify-center border
                  ${achievement?.isUnlocked
                    ? 'bg-accent/20 border-accent' :'bg-muted/20 border-muted'
                  }
                `}
              >
                <Icon
                  name={achievement?.icon}
                  size={24}
                  color={achievement?.isUnlocked ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className={`font-heading font-semibold text-sm md:text-base ${achievement?.isUnlocked ? 'text-accent' : 'text-muted-foreground'}`}>
                    {achievement?.name}
                  </h4>
                  {achievement?.isUnlocked && (
                    <Icon name="Check" size={16} color="var(--color-accent)" />
                  )}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground font-caption mb-2">
                  {achievement?.description}
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted/30 rounded-sm overflow-hidden">
                    <div
                      className={`h-full transition-all duration-base ${achievement?.isUnlocked ? 'bg-accent' : 'bg-primary'}`}
                      style={{ width: `${(achievement?.progress / achievement?.required) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-data text-muted-foreground whitespace-nowrap">
                    {achievement?.progress}/{achievement?.required}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressionTracker;